/**
 * Live ticker — reduces the global `/ws/orders/` firehose into a few
 * rate metrics that are useful in the Topbar / Live Ops banner without
 * keeping every order in memory.
 *
 * Buckets are minute-aligned in the user's local clock so "orders/min"
 * looks the way operators expect; when the system clock wobbles we lose
 * a sample, never a count.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWebSocket } from '@/api/ws';

export interface LiveTicker {
  ordersPerMin: number;
  gmvPerMin: number;
  exceptionsPerMin: number;
  lastEventAt: number | null;
  /** Underlying WS connection state for status pills. */
  status: ReturnType<typeof useWebSocket>['status'];
}

interface OrderEvent {
  type?: string;
  order?: {
    id?: number;
    status?: string;
    final_total?: number | string;
    total?: number | string;
    late_tags?: unknown;
  };
}

interface Bucket {
  ts: number;
  orders: number;
  gmv: number;
  exceptions: number;
}

const WINDOW_BUCKETS = 5; // 5 × 60s rolling window

export function useLiveTicker(): LiveTicker {
  const [bucket, setBucket] = useState<Bucket>(() => makeBucket());
  const [history, setHistory] = useState<Bucket[]>([]);
  const lastEventRef = useRef<number | null>(null);

  // Roll buckets once a minute so per-min metrics stay accurate even when
  // there's no traffic — without this an idle store would freeze the rate.
  useEffect(() => {
    const t = window.setInterval(() => {
      setHistory((h) => {
        const next = [...h, bucket].slice(-WINDOW_BUCKETS);
        return next;
      });
      setBucket(makeBucket());
    }, 60_000);
    return () => window.clearInterval(t);
  }, [bucket]);

  const onMessage = useCallback((ev: OrderEvent) => {
    if (!ev) return;
    const isCreate = ev.type === 'order_created';
    const isUpdate = ev.type === 'order_updated';
    if (!isCreate && !isUpdate) return;

    lastEventRef.current = Date.now();
    setBucket((b) => {
      const o = ev.order ?? {};
      const gmv = num(o.final_total) ?? num(o.total) ?? 0;
      const isException =
        o.status === 'cancelled' ||
        Array.isArray(o.late_tags)
          ? (o.late_tags as unknown[]).length > 0
          : !!o.late_tags;
      return {
        ...b,
        orders: b.orders + (isCreate ? 1 : 0),
        gmv: b.gmv + (isCreate ? gmv : 0),
        exceptions: b.exceptions + (isException ? 1 : 0),
      };
    });
  }, []);

  const { status } = useWebSocket<OrderEvent>({
    path: '/ws/orders/',
    onMessage,
  });

  const totals = useMemo(() => {
    const acc = history.reduce(
      (a, b) => ({
        orders: a.orders + b.orders,
        gmv: a.gmv + b.gmv,
        exceptions: a.exceptions + b.exceptions,
      }),
      { orders: 0, gmv: 0, exceptions: 0 },
    );
    const denom = Math.max(1, history.length);
    return {
      ordersPerMin: Math.round(acc.orders / denom),
      gmvPerMin: Math.round(acc.gmv / denom),
      exceptionsPerMin: Math.round(acc.exceptions / denom),
    };
  }, [history]);

  return {
    ...totals,
    lastEventAt: lastEventRef.current,
    status,
  };
}

function makeBucket(): Bucket {
  return { ts: Date.now(), orders: 0, gmv: 0, exceptions: 0 };
}

function num(v: number | string | undefined | null): number | null {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
